import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Title1, Subtitle2, TabList, Tab, MessageBar } from '@fluentui/react-components'
import { levelForService, useMe } from '../lib/me'
import { SERVICE_LABELS } from '../lib/services'
import ServiceLicensesTab from '../components/ServiceLicensesTab'
import UserLinksTab from '../components/UserLinksTab'
import ServiceSettingsTab from '../components/ServiceSettingsTab'

// 'types' (License types) used to be its own tab — it's config, same as everything else here,
// so it moved into Settings as a section instead of a fourth top-level tab. Kept a redirect from
// the old value below rather than just letting it 404 into the default, since Dashboard/bookmarks
// may still reference `?tab=types`.
type TabValue = 'licenses' | 'links' | 'settings'

const VALID_TABS: TabValue[] = ['licenses', 'links', 'settings']

export default function ServiceConfig() {
  const { serviceId = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [refreshKey, setRefreshKey] = useState(0)
  const me = useMe()
  const level = levelForService(me, serviceId)
  const canManage = level === 'manager'

  const requestedTab = searchParams.get('tab')
  let activeTab: TabValue = VALID_TABS.includes(requestedTab as TabValue)
    ? (requestedTab as TabValue)
    : requestedTab === 'types'
      ? 'settings'
      : 'licenses'
  // A read-only collaborator can't use the Settings tab even if it's in the URL.
  if (activeTab === 'settings' && !canManage) activeTab = 'licenses'

  function handleTabSelect(value: TabValue) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', value)
      return next
    })
  }

  if (!level) {
    return (
      <div>
        <Title1>{SERVICE_LABELS[serviceId] ?? serviceId}</Title1>
        <MessageBar intent="warning" style={{ marginTop: 16 }}>
          You don't have access to this service. Ask an administrator to grant it on Settings →
          Access.
        </MessageBar>
      </div>
    )
  }

  return (
    <div>
      <Title1>{SERVICE_LABELS[serviceId] ?? serviceId}</Title1>
      <Subtitle2 style={{ display: 'block', marginTop: 4, marginBottom: 24 }}>
        Licenses, sync, and configuration
      </Subtitle2>

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_e, data) => handleTabSelect(data.value as TabValue)}
        style={{ marginBottom: 16 }}
      >
        <Tab value="licenses">Licenses</Tab>
        <Tab value="links">User links</Tab>
        {canManage && <Tab value="settings">Settings</Tab>}
      </TabList>

      {activeTab === 'licenses' && <ServiceLicensesTab serviceId={serviceId} refreshKey={refreshKey} />}
      {activeTab === 'links' && <UserLinksTab serviceId={serviceId} refreshKey={refreshKey} canManage={canManage} />}
      {activeTab === 'settings' && canManage && (
        <ServiceSettingsTab serviceId={serviceId} refreshKey={refreshKey} onChanged={() => setRefreshKey(k => k + 1)} />
      )}
    </div>
  )
}
